import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BoardBinding, BoardMetricKey } from 'chery-contracts';
import { useMemo, useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { Badge } from '../../components/ui/Badge';
import { Notice } from '../../components/ui/Notice';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { Table, TableCell, TableHead, TableHeaderCell, TableRow } from '../../components/ui/Table';
import { cn } from '../../lib/cn';
import { serverMessage } from '../../lib/form-helpers';
import { PILLAR_META } from '../../lib/pillars';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { compatibleKpis, expectedUnitText } from './board-display';

/**
 * Setup → Board bindings (plan §7, §8.1; ADMIN/PM via `RequireRole`): every metric key with its expected unit and
 * currency, and a KPI select limited to matching KPIs. Unbound rows are highlighted; choosing "Unbound" unbinds.
 * The service re-checks every binding (400 naming expected vs actual), so the select's filter is a convenience.
 */
export const BoardBindingsPage = () => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const bindings = useQuery(orpc.board.bindings.list.queryOptions({ input: {} }));
  const kpis = useQuery(orpc.kpis.list.queryOptions({ input: { includeInactive: true } }));
  const update = useMutation(orpc.board.bindings.update.mutationOptions());
  const items = useMemo(() => bindings.data?.items ?? [], [bindings.data]);
  const unboundCount = items.filter((item) => item.kpi === null).length;

  const unitLabel = (unit: BoardBinding['expectedUnit']): string => t(`enums.unit.${unit}`);

  const onChange = async (key: BoardMetricKey, kpiId: string): Promise<void> => {
    setNotice(null);

    try {
      const saved = await update.mutateAsync({ key, kpiId: kpiId === '' ? null : kpiId });

      setNotice({
        tone: 'success',
        text: saved.kpi ? t('board.bindings.saved', { key, name: saved.kpi.name }) : t('board.bindings.unboundSaved', { key }),
      });
      await queryClient.invalidateQueries({ queryKey: orpc.board.key() });
    } catch (error) {
      setNotice({ tone: 'danger', text: serverMessage(error) ?? t('errors.saveFailed') });
    }
  };

  if (bindings.isPending || kpis.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner />
        {t('app.loading')}
      </div>
    );
  }

  if (bindings.isError || kpis.isError) {
    return <Notice tone="danger">{t('errors.loadFailed')}</Notice>;
  }

  const allKpis = kpis.data.items;

  return (
    <div
      className="space-y-4"
      data-board-bindings
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('pages.setupBoardBindings')}</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t('board.bindings.subtitle')}</p>
        </div>
        <Badge
          tone={unboundCount > 0 ? 'danger' : 'muted'}
          className="text-xs"
        >
          {unboundCount > 0 ? t('board.bindings.unboundCount', { count: unboundCount, total: items.length }) : t('board.bindings.allBound')}
        </Badge>
      </div>

      {notice ? (
        <Notice
          tone={notice.tone}
          onDismiss={() => setNotice(null)}
        >
          <span data-binding-notice>{notice.text}</span>
        </Notice>
      ) : null}

      <Table>
        <TableHead>
          <tr>
            <TableHeaderCell>{t('board.bindings.columns.key')}</TableHeaderCell>
            <TableHeaderCell>{t('board.bindings.columns.expected')}</TableHeaderCell>
            <TableHeaderCell>{t('board.bindings.columns.aggregation')}</TableHeaderCell>
            <TableHeaderCell>{t('board.bindings.columns.feeds')}</TableHeaderCell>
            <TableHeaderCell className="w-80">{t('board.bindings.columns.kpi')}</TableHeaderCell>
          </tr>
        </TableHead>
        <tbody>
          {items.map((item) => {
            const options = compatibleKpis(item, allKpis);
            // Keep a bound KPI selectable even if the list scope hides it, so the select never lies.
            const current = item.kpi && !options.some((kpi) => kpi.id === item.kpi?.id) ? [item.kpi] : [];

            return (
              <TableRow
                key={item.key}
                className={cn(item.kpi === null && 'bg-destructive/5')}
                data-binding-row={item.key}
                data-bound={item.kpi ? 'true' : 'false'}
              >
                <TableCell>
                  <p className="font-mono text-xs text-foreground">{item.key}</p>
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs">{expectedUnitText(item.expectedUnit, item.expectedCurrency, unitLabel)}</TableCell>
                <TableCell className="text-xs">{t(`board.sheets.aggregation.${item.aggregation}`)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {item.feeds.length > 0 ? item.feeds.join(' · ') : t('board.bindings.feedsNothing')}
                </TableCell>
                <TableCell>
                  <Select
                    aria-label={`${item.key} KPI`}
                    data-binding-select={item.key}
                    value={item.kpi?.id ?? ''}
                    disabled={update.isPending}
                    hasError={item.kpi === null}
                    options={[
                      { value: '', label: options.length === 0 ? t('board.bindings.noCompatible') : t('board.bindings.none') },
                      ...[...current, ...options].map((kpi) => ({
                        value: kpi.id,
                        label: `${PILLAR_META[kpi.pillar].shortLabel} · ${kpi.name} (${kpi.code})${kpi.status === 'INACTIVE' ? ` — ${t('board.bindings.inactive')}` : ''}`,
                      })),
                    ]}
                    onChange={(event) => void onChange(item.key, event.target.value)}
                    className="h-8 text-xs"
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};
